import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { MdInfoOutline, MdKeyboardArrowDown, MdLock } from "react-icons/md";
import paths from "../../../utils/constants";
import imagePaths from "../../../assets/imagePaths";
import api from "../../../api/api";
import { logMessage } from "../../../utils/helpers";
import { cn } from "../../../lib/utils";
import { getPointsProgressDisplay } from "../../../utils/gamificationProgress";

const PURPLE = "#552F83";
const PAGE_BG = "#F1ECFF";
const EMPTY_TIER_ROADMAP = [];

function isApiSuccess(res) {
  if (res == null) return false;
  const s = res.status;
  return s === 1 || s === "1" || Number(s) === 1;
}

function mergeProgressSummary(baseSummary, progressPayload) {
  if (!progressPayload || typeof progressPayload !== "object") {
    return baseSummary && typeof baseSummary === "object" ? baseSummary : {};
  }
  const tier = progressPayload.current_tier || {};
  const s = baseSummary && typeof baseSummary === "object" ? { ...baseSummary } : {};
  return {
    ...s,
    total_xp: Number(
      progressPayload.total_xp != null ? progressPayload.total_xp : s.total_xp ?? 0
    ),
    current_level: Number(
      progressPayload.current_level != null ? progressPayload.current_level : s.current_level ?? 1
    ),
    level_title: progressPayload.level_title || s.level_title,
    current_xp:
      progressPayload.current_xp != null && progressPayload.current_xp !== ""
        ? Number(progressPayload.current_xp)
        : s.current_xp,
    xp_required_for_completion:
      progressPayload.xp_required_for_completion != null &&
      progressPayload.xp_required_for_completion !== ""
        ? Number(progressPayload.xp_required_for_completion)
        : s.xp_required_for_completion,
    points_away_text: progressPayload.points_away_text || s.points_away_text,
    current_tier: {
      name:
        tier.name != null && String(tier.name).length > 0
          ? tier.name
          : s.current_tier?.name || "",
      key: String(tier.key || s.current_tier?.key || "bronze").toLowerCase(),
      icon: tier.icon || s.current_tier?.icon || "",
    },
  };
}

function JourneyTierCurrentIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="shrink-0"
      aria-hidden
    >
      <rect
        x="0.25"
        y="0.25"
        width="11.5"
        height="11.5"
        rx="5.75"
        stroke="#B58F08"
        strokeWidth="0.5"
        strokeLinecap="round"
        strokeLinejoin="bevel"
        strokeDasharray="2 2"
      />
      <path
        d="M3.375 6.375L5.125 8.125L8.625 4.375"
        stroke="#B58F08"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function JourneyTierCompletedIcon() {
  const uid = useId().replace(/:/g, "");
  const clipId = `jt-done-${uid}`;
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="shrink-0"
      aria-hidden
    >
      <g clipPath={`url(#${clipId})`}>
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M0 6C0 4.4087 0.632141 2.88258 1.75736 1.75736C2.88258 0.632141 4.4087 0 6 0C7.5913 0 9.11742 0.632141 10.2426 1.75736C11.3679 2.88258 12 4.4087 12 6C12 7.5913 11.3679 9.11742 10.2426 10.2426C9.11742 11.3679 7.5913 12 6 12C4.4087 12 2.88258 11.3679 1.75736 10.2426C0.632141 9.11742 0 7.5913 0 6ZM5.6576 8.568L9.112 4.2496L8.488 3.7504L5.5424 7.4312L3.456 5.6928L2.944 6.3072L5.6576 8.568Z"
          fill="#00934C"
        />
      </g>
      <defs>
        <clipPath id={clipId}>
          <rect width="12" height="12" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

const LevelJourneyPanel = ({ startLoading, stopLoading }) => {
  const navigate = useNavigate();
  const [payload, setPayload] = useState(null);
  /** Which expandable tiers show level list; current tier open by default (see effect below). */
  const [expandedTierKeys, setExpandedTierKeys] = useState(() => new Set());
  const defaultExpandAppliedRef = useRef(false);
  const roadmapTrackRef = useRef(null);
  const timelineGrayLineRef = useRef(null);
  const tierIconRefs = useRef([]);
  const measureTimelineProgressRef = useRef(() => {});
  const [timelineProgressPx, setTimelineProgressPx] = useState(0);

  const loadersRef = useRef({ startLoading, stopLoading });
  loadersRef.current = { startLoading, stopLoading };

  const load = useCallback(async () => {
    const { startLoading: sl, stopLoading: el } = loadersRef.current;
    sl?.();
    try {
      const tz =
        typeof Intl !== "undefined" && Intl.DateTimeFormat
          ? Intl.DateTimeFormat().resolvedOptions().timeZone
          : "UTC";
      const journeyBody = {
        timezone: tz,
        ...(process.env.REACT_APP_LEVEL_JOURNEY_DUMMY === "true"
          ? { use_dummy_level_journey: true }
          : {}),
      };
      const [journeyRes, progressRes] = await Promise.allSettled([
        api.getLevelJourney(journeyBody),
        api.getProgress(),
      ]);

      let summary = null;
      let tier_roadmap = [];
      let journeyDummy = false;

      if (journeyRes.status === "fulfilled" && isApiSuccess(journeyRes.value) && journeyRes.value.data) {
        const d = journeyRes.value.data;
        journeyDummy = d._journey_dummy === true;
        summary = d.summary || null;
        tier_roadmap = Array.isArray(d.tier_roadmap) ? d.tier_roadmap : [];
      } else {
        logMessage(
          journeyRes.status === "rejected"
            ? journeyRes.reason
            : `getLevelJourney: ${journeyRes.value?.message || "failed"}`,
          "warn"
        );
      }

      if (
        !journeyDummy &&
        progressRes.status === "fulfilled" &&
        isApiSuccess(progressRes.value) &&
        progressRes.value.data
      ) {
        summary = mergeProgressSummary(summary || {}, progressRes.value.data);
      }

      setPayload({ summary: summary || {}, tier_roadmap });
    } catch (e) {
      logMessage(e);
      setPayload({ summary: {}, tier_roadmap: [] });
    } finally {
      el?.();
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!payload?.tier_roadmap?.length || defaultExpandAppliedRef.current) return;
    const current = payload.tier_roadmap.find((t) => t.status === "current");
    if (current?.key) {
      setExpandedTierKeys((prev) => new Set(prev).add(current.key));
      defaultExpandAppliedRef.current = true;
    }
  }, [payload?.tier_roadmap]);

  const summary = payload?.summary || {};
  const tier_roadmap = Array.isArray(payload?.tier_roadmap)
    ? payload.tier_roadmap
    : EMPTY_TIER_ROADMAP;

  const measureTimelineProgress = useCallback(() => {
    const grayEl = timelineGrayLineRef.current;
    const list = tier_roadmap;
    if (!grayEl || list.length < 2) {
      setTimelineProgressPx(0);
      return;
    }
    const currentIdx = list.findIndex((r) => r.status === "current");
    const targetIdx =
      currentIdx >= 0 ? currentIdx : Math.max(0, list.length - 1);
    const iconEl = tierIconRefs.current[targetIdx];
    if (!iconEl) {
      setTimelineProgressPx(0);
      return;
    }
    const g = grayEl.getBoundingClientRect();
    const i = iconEl.getBoundingClientRect();
    const h = Math.max(0, Math.round(i.top + i.height / 2 - g.top));
    setTimelineProgressPx((prev) => (prev === h ? prev : h));
  }, [tier_roadmap]);

  useEffect(() => {
    measureTimelineProgressRef.current = measureTimelineProgress;
  }, [measureTimelineProgress]);

  /** Throttled to one read per frame so the purple line tracks grid accordion without 280ms end-delay. */
  useLayoutEffect(() => {
    measureTimelineProgress();
    const root = roadmapTrackRef.current;
    let roRafId = null;
    const flushRo = () => {
      roRafId = null;
      measureTimelineProgressRef.current();
    };
    const onRo = () => {
      if (roRafId != null) return;
      roRafId = requestAnimationFrame(flushRo);
    };
    const onWinResize = () => measureTimelineProgressRef.current();

    let ro = null;
    if (root) {
      ro = new ResizeObserver(onRo);
      ro.observe(root);
    }
    window.addEventListener("resize", onWinResize);
    return () => {
      if (roRafId != null) cancelAnimationFrame(roRafId);
      ro?.disconnect();
      window.removeEventListener("resize", onWinResize);
    };
  }, [measureTimelineProgress]);

  useLayoutEffect(() => {
    measureTimelineProgressRef.current();
    const id = requestAnimationFrame(() => measureTimelineProgressRef.current());
    return () => cancelAnimationFrame(id);
  }, [expandedTierKeys]);

  const tier = summary.current_tier || {};
  const tierKey = String(tier.key || "bronze").toLowerCase();
  const fallbackTierIconByKey = {
    bronze: imagePaths.bronze,
    silver: imagePaths.silver,
    gold: imagePaths.gold,
    diamond: imagePaths.crown,
  };
  const tierIcon =
    tier.icon || fallbackTierIconByKey[tierKey] || imagePaths.medal || imagePaths.crown;
  const tierName = tier.name || "Bronze Tier";
  const currentLevel = Number(summary.current_level || 1);
  const { progressPercent, fractionLabel } = getPointsProgressDisplay(
    summary.current_xp,
    summary.xp_required_for_completion
  );

  return (
    <div
      className="p-4 relative md:p-5 min-h-full pb-10"
      style={{ backgroundColor: PAGE_BG }}
    >
      {/* <div
        className="absolute inset-0 h-48 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: "url('/bgImage.png')" }}
        aria-hidden
      /> */}
      <div
        className="absolute inset-0 bg-gradient-to-b from-[#3d2660]/92 via-[#552F83]/78 to-[#6b4d96]/45 pointer-events-none"
        aria-hidden
      />
      <div className="relative z-10 px-3 pb-6 md:px-5 md:pb-8">
        <p className="text-[12px] text-[#323232] mb-4 drop-shadow-sm">
          <button
            type="button"
            className="cursor-pointer hover:underline text-[#323232] bg-transparent border-0 p-0 font-inherit"
            onClick={() => navigate(paths.YOUR_PROGRESS)}
          >
            Your progress
          </button>
          <span className="mx-1.5 opacity-90 text-[#323232]">&gt;</span>
          <span className="font-medium text-[#323232]">Levels</span>
        </p>

        <div
          className="relative mb-4 overflow-hidden rounded-3xl p-5 text-white md:p-6"
          style={{
            backgroundImage: "url('/bgImage.webp')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="mb-2 flex items-center gap-3">
                <span className="text-xl font-semibold leading-none">Level {currentLevel}</span>
                <span className="rounded-full border border-solid border-[#ccc] bg-[#FFFFFF1A] px-4 py-1 text-xl font-semibold leading-none">
                  {tierName}
                </span>
              </div>
              <p className="text-xl font-normal leading-none text-[#FFFFFF]">
                {summary.level_title || ""}
              </p>
            </div>
            <img alt="" src={tierIcon} className="h-20 w-20 shrink-0 object-contain" />
          </div>

          <div className="mt-7">
            <div className="mb-2 flex items-center justify-between text-xl">
              <span>Points Progress</span>
              <span>{fractionLabel}</span>
            </div>
            <div className="h-3 overflow-hidden rounded-full bg-white/35">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${progressPercent}%`,
                  background: "linear-gradient(182.35deg, #FFFFFF -57.18%, #F3E6FF 91.87%)",
                  transition: "width 0.4s ease",
                }}
              />
            </div>
            <p className="mt-3 text-center text-xl font-normal text-[#FFFFFF]">
              {summary.points_away_text ?? ""}
            </p>
          </div>
        </div>

        <div className="bg-white rounded-3xl shadow-sm border border-[#E8E0F5] p-4 md:p-5 mb-4">
          <div className="flex items-center justify-between gap-2 mb-4">
            <span className="text-base font-bold text-[#2f2f39]">Tiers Roadmap</span>
            <button
              type="button"
              className="inline-flex items-center gap-1 text-xs font-semibold text-[#552F83] bg-transparent border-0 p-0 cursor-pointer hover:underline"
            >
              <MdInfoOutline className="w-4 h-4" />
              How tiers works?
            </button>
          </div>

          <div
            ref={roadmapTrackRef}
            className="relative rounded-2xl border border-[#EDE8F7] bg-[#fff] p-3 md:p-4"
          >
            {tier_roadmap.length > 1 && (
              <>
                <div
                  ref={timelineGrayLineRef}
                  className="pointer-events-none absolute bottom-16 left-[calc(0.75rem+1.875rem)] top-5 z-0 w-px bg-[#DADADA] md:left-[calc(1.3rem+1.875rem)]"
                  aria-hidden
                />
                {timelineProgressPx > 0 && (
                  <div
                    className="pointer-events-none absolute left-[calc(0.75rem+1.875rem)] top-5 z-[1] w-px bg-[#552F83] md:left-[calc(1.3rem+1.875rem)]"
                    style={{ height: timelineProgressPx }}
                    aria-hidden
                  />
                )}
              </>
            )}
            <ul className="relative z-[2] m-0 flex list-none flex-col gap-5 p-0 md:gap-6">
              {tier_roadmap.map((row, idx) => {
                const tierExpandable =
                  row.status === "current" || row.status === "completed";
                const expanded = tierExpandable && expandedTierKeys.has(row.key);
                const isCompletedTier = row.status === "completed";

                const toggleTier = () => {
                  if (!tierExpandable) return;
                  setExpandedTierKeys((prev) => {
                    const next = new Set(prev);
                    if (next.has(row.key)) next.delete(row.key);
                    else next.add(row.key);
                    return next;
                  });
                };

                return (
                  <li key={row.key || idx} className="flex gap-3 md:gap-4 items-stretch">
                    <div
                      className={cn(
                        "relative z-[3] flex w-[3.75rem] shrink-0 flex-col items-center justify-start self-stretch rounded-xl px-1.5 py-0 transition-colors",
                        
                      )}
                    >
                      <div
                        ref={(el) => {
                          tierIconRefs.current[idx] = el;
                        }}
                        className="tier_icon flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl border-2 border-white bg-[#F2F2F2] shadow-sm"
                      >
                        {row.icon ? (
                          <img src={row.icon} alt="" className="w-8 h-8 object-contain" />
                        ) : (
                          <span className="text-xs font-bold text-[#7C7790]">
                            {String(row.key || "?").slice(0, 1).toUpperCase()}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div
                        className={cn(
                          "rounded-2xl border border-[#E8E0F5] bg-white overflow-hidden",
                          row.status === "current" && "ring-1 ring-[#552F83]/15"
                        )}
                        style={{ boxShadow: "0px 1px 6.8px 0px #0000000D" }}
                      >
                        <button
                          type="button"
                          disabled={!tierExpandable}
                          className={cn(
                            "w-full flex items-center gap-3 p-3 md:p-4 pb-0 md:pb-0 text-left border-0 bg-transparent",
                            tierExpandable && "cursor-pointer hover:bg-[#FAF8FF] pb-0 md:pb-0", expanded && tierExpandable && " hover:bg-transparent"
                          )}
                          onClick={toggleTier}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-col gap-1.5">
                            <span className="text-xs text-[#7a7391] font-medium">
                                {row.level_range_label}
                              </span>
                              <span className="text-[#323232] font-semibold text-[15px]">{row.name}</span>
                            </div>
                            {row.status === "completed" && row.points_to_unlock_text && (
                              <p className="text-xs text-[#00934C] mt-1.5 flex items-center gap-1.5 font-semibold">
                                <JourneyTierCompletedIcon />
                                {row.points_to_unlock_text}
                              </p>
                            )}
                            {row.status === "current" && row.points_to_unlock_text && (
                              <p className="text-xs text-[#B58F08] mt-1.5 flex items-center gap-1.5 font-semibold">
                                <JourneyTierCurrentIcon />
                                {row.points_to_unlock_text}
                              </p>
                            )}
                            {row.status === "locked" && row.points_to_unlock_text && (
                              <p className="text-xs text-[#7a7391] mt-1.5">
                                {row.points_to_unlock_text}
                              </p>
                            )}
                          </div>
                          <div className="shrink-0 self-start pt-4">
                            {row.status === "locked" ? (
                              <MdLock className="w-6 h-6 text-[#9ca3af]" />
                            ) : (
                              <span
                                className={cn(
                                  "inline-flex text-[#323232] transition-transform duration-[280ms] motion-reduce:transition-none motion-reduce:duration-0 [transition-timing-function:cubic-bezier(0.25,0.1,0.25,1)]",
                                  expanded && "rotate-180"
                                )}
                                aria-hidden
                              >
                                <MdKeyboardArrowDown className="h-6 w-6" />
                              </span>
                            )}
                          </div>
                        </button>

                        {tierExpandable && (
                          <div
                            className={cn(
                              "grid transition-[grid-template-rows] duration-[280ms] motion-reduce:transition-none motion-reduce:duration-0 [transition-timing-function:cubic-bezier(0.25,0.1,0.25,1)]",
                              expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                            )}
                            onTransitionEnd={(e) => {
                              if (e.target !== e.currentTarget) return;
                              if (e.propertyName !== "grid-template-rows") return;
                              measureTimelineProgressRef.current();
                            }}
                            onTransitionCancel={(e) => {
                              if (e.target !== e.currentTarget) return;
                              measureTimelineProgressRef.current();
                            }}
                          >
                            <div
                              className={cn(
                                "min-h-0 overflow-hidden",
                                !expanded && "pointer-events-none"
                              )}
                              aria-hidden={!expanded}
                            >
                              <div className="box-border w-full min-w-0 border-t border-[#ECE5F8] bg-white p-3 md:p-4">
                                <div className="box-border w-full min-w-0 rounded-xl bg-[#F1ECFF40] p-2 md:p-3">
                                  <ul className="m-0 w-full min-w-0 max-w-full list-none space-y-2 p-0">
                                    {(row.levels || []).map((lv) => (
                                      <li
                                        key={lv.level_number}
                                        className="box-border flex w-full min-w-0 max-w-full items-center gap-3 rounded-xl border border-[#E8E0F5] bg-white px-3 py-2.5"
                                      >
                                        <div
                                          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-xs font-bold text-[#552F83]"
                                          style={{ backgroundColor: "#E2DFFF" }}
                                        >
                                          {lv.level_number}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                          <div className="flex flex-col gap-0">
                                            <span className="text-sm font-medium capitalize text-[#323232]">
                                              {lv.title}
                                            </span>
                                            <span className="mt-0.5 text-xs text-[#7a7391]">
                                              {lv.xp_required_for_completion_text}
                                            </span>
                                          </div>
                                        </div>
                                        <div className="min-w-[5.5rem] shrink-0 text-right text-xs">
                                          {lv.status === "completed" && (
                                            <div>
                                              <span className="block text-sm font-medium text-[#00934C]">
                                                Completed
                                              </span>
                                              {lv.completed_at && (
                                                <span className="mt-0.5 text-xs font-normal text-[#979797]">
                                                  {lv.completed_at}
                                                </span>
                                              )}
                                            </div>
                                          )}
                                          {lv.status === "in_progress" && (
                                            <span className="text-sm font-medium text-[#B58F08]">
                                              In Progress
                                            </span>
                                          )}
                                          {lv.status === "locked" && (
                                            <MdLock className="ml-auto inline-block h-5 w-5 text-[#9ca3af]" />
                                          )}
                                        </div>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        <div className="rounded-2xl border border-[#E8E0F5] px-4 py-3 flex items-start gap-2 shadow-sm" style={{ background: "linear-gradient(273.73deg, #FFFFFF -14.43%, #EEECFF 188.05%)" }}>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M10.713 14.713C10.9043 14.521 11 14.2833 11 14V10C11 9.71667 10.904 9.47933 10.712 9.288C10.52 9.09667 10.2827 9.00067 10 9C9.71733 8.99933 9.48 9.09533 9.288 9.288C9.096 9.48067 9 9.718 9 10V14C9 14.2833 9.096 14.521 9.288 14.713C9.48 14.905 9.71733 15.0007 10 15C10.2827 14.9993 10.5203 14.9043 10.713 14.713ZM10.713 6.712C10.9043 6.52067 11 6.28333 11 6C11 5.71667 10.904 5.47933 10.712 5.288C10.52 5.09667 10.2827 5.00067 10 5C9.71733 4.99933 9.48 5.09533 9.288 5.288C9.096 5.48067 9 5.718 9 6C9 6.282 9.096 6.51967 9.288 6.713C9.48 6.90633 9.71733 7.002 10 7C10.2827 6.998 10.5203 6.902 10.713 6.712ZM10 20C8.61667 20 7.31667 19.7373 6.1 19.212C4.88334 18.6867 3.825 17.9743 2.925 17.075C2.025 16.1757 1.31267 15.1173 0.788001 13.9C0.263335 12.6827 0.000667933 11.3827 1.26582e-06 10C-0.000665401 8.61733 0.262001 7.31733 0.788001 6.1C1.314 4.88267 2.02633 3.82433 2.925 2.925C3.82367 2.02567 4.882 1.31333 6.1 0.788C7.318 0.262667 8.618 0 10 0C11.382 0 12.682 0.262667 13.9 0.788C15.118 1.31333 16.1763 2.02567 17.075 2.925C17.9737 3.82433 18.6863 4.88267 19.213 6.1C19.7397 7.31733 20.002 8.61733 20 10C19.998 11.3827 19.7353 12.6827 19.212 13.9C18.6887 15.1173 17.9763 16.1757 17.075 17.075C16.1737 17.9743 15.1153 18.687 13.9 19.213C12.6847 19.739 11.3847 20.0013 10 20Z" fill="#552F83"/>
</svg>

          <p className="text-sm text-[#323232] leading-snug m-0">
            Levels grow as you continue painting over time. Your level determines which tier you belong
            to.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LevelJourneyPanel;
